import { useGT } from 'gt-react';
import { useSelector } from '@tanstack/react-store';
import {
  ArTaskPdfColumnIdSchema,
  type ArTaskRow,
  type DownloadArTasksPdfInput,
} from '@erp/rpc';
import type { ColumnDef, Table } from '@/lib/tanstack-table';
import { useDownloadArTasksPdf } from '@/api/rpc/hooks/accounts-receivable/useArBillingTasks';
import { useToast } from 'src/context/ToastContext';
import { arTasksColumnLabels } from './arTasksColumns';

/**
 * The PDF's columns: the table's visible data columns in their on-screen
 * order, with their header text and width. Non-data columns (select) drop
 * out.
 */
export function arTasksPdfColumns(
  visible: ReadonlyArray<{ id: string; getSize: () => number }>,
  labels: Record<string, string>,
): DownloadArTasksPdfInput['columns'] {
  return visible.flatMap((column) => {
    const parsed = ArTaskPdfColumnIdSchema.safeParse(column.id);
    if (!parsed.success) return [];
    return [
      {
        id: parsed.data,
        label: labels[parsed.data] ?? parsed.data,
        width: column.getSize(),
      },
    ];
  });
}

/**
 * Download the selected tasks as a PDF that mirrors the visible column
 * layout, in the table's row order.
 *
 * The React Compiler treats `table` as immutable (its identity never
 * changes), so it would cache the first column snapshot and miss later
 * hides, moves and resizes. `'use no memo'` re-derives on every render, and
 * the layout subscriptions re-render when any of them change.
 */
export function useArTasksPdfDownload({
  table,
  columns,
  selectedRows,
}: {
  table: Table<ArTaskRow>;
  columns: ColumnDef<ArTaskRow>[];
  selectedRows: ArTaskRow[];
}) {
  'use no memo';
  // Subscribe only; the getters below derive from the same state.
  useSelector(table.atoms.columnVisibility);
  useSelector(table.atoms.columnOrder);
  useSelector(table.atoms.columnSizing);
  const gt = useGT();
  const { showToast } = useToast();
  const download = useDownloadArTasksPdf();
  // At render, so the button can disable itself when every data column is
  // hidden (an empty column list fails the contract's min(1)).
  const pdfColumns = arTasksPdfColumns(
    table.getVisibleLeafColumns(),
    arTasksColumnLabels(columns),
  );

  async function downloadPdf() {
    try {
      await download.mutateAsync({
        taskIds: selectedRows.map((row) => row.taskId),
        columns: pdfColumns,
      });
    } catch (err) {
      showToast({
        title: gt('Download failed', {
          context: 'toast title, AR tasks PDF download failed',
        }),
        message:
          err instanceof Error
            ? err.message
            : gt('Could not generate the PDF', {
                context: 'toast message, AR tasks PDF download failed',
              }),
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return {
    downloadPdf,
    pdfDisabled: download.isPending || pdfColumns.length === 0,
  };
}
